import { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { NavMenuNode } from '@/api/platform-nav-menu';
import { NavIcon } from '@/components/nav/NavIcon';
import { BrandLogo } from './BrandLogo';
import { cn } from '@/lib/utils';

const makeLinkClass =
  (isLight: boolean) =>
  ({ isActive }: { isActive: boolean }) =>
    cn(
      'mx-3 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 motion-reduce:transition-none',
      isActive
        ? isLight
          ? 'border border-cyan-500/35 bg-cyan-100/70 text-cyan-900 shadow-[0_6px_20px_-16px_rgba(8,145,178,0.45)]'
          : 'border border-cyan-500/25 bg-cyan-500/10 text-cyan-100 shadow-[0_0_32px_-12px_rgba(34,211,238,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]'
        : isLight
          ? 'border border-transparent text-slate-600 hover:border-cyan-500/20 hover:bg-white hover:text-slate-900'
          : 'border border-transparent text-zinc-400 hover:border-white/5 hover:bg-white/[0.04] hover:text-zinc-100',
    );

function subtreeHasActivePath(node: NavMenuNode, pathname: string): boolean {
  if (node.path && (pathname === node.path || pathname.startsWith(`${node.path}/`))) {
    return true;
  }
  return node.children.some((c) => subtreeHasActivePath(c, pathname));
}

function collectMenuPaths(nodes: NavMenuNode[]): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    if (n.path) out.push(n.path);
    out.push(...collectMenuPaths(n.children));
  }
  return out;
}

export function PlatformSidebarNav({
  tree,
  onNavigate,
  isLight = false,
}: {
  tree: NavMenuNode[];
  onNavigate?: () => void;
  isLight?: boolean;
}) {
  const location = useLocation();
  const linkClass = makeLinkClass(isLight);
  const allPaths = useMemo(() => collectMenuPaths(tree), [tree]);
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const next: Record<string, boolean> = {};
    const markOpen = (nodes: NavMenuNode[]) => {
      for (const n of nodes) {
        if (n.menuType === 'DIRECTORY' && n.children.length) {
          if (subtreeHasActivePath(n, location.pathname)) {
            next[n.id] = true;
          }
          markOpen(n.children);
        }
      }
    };
    markOpen(tree);
    setOpenMap((prev) => ({ ...prev, ...next }));
  }, [location.pathname, tree]);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    const wasIn = allPaths.some(
      (p) => prev === p || prev.startsWith(`${p}/`),
    );
    const nowIn = allPaths.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );
    if (nowIn && !wasIn) {
      const next: Record<string, boolean> = {};
      const openActiveDirs = (nodes: NavMenuNode[]) => {
        for (const n of nodes) {
          if (n.menuType === 'DIRECTORY' && subtreeHasActivePath(n, location.pathname)) {
            next[n.id] = true;
          }
          if (n.children.length) openActiveDirs(n.children);
        }
      };
      openActiveDirs(tree);
      setOpenMap((o) => ({ ...o, ...next }));
    }
  }, [location.pathname, allPaths, tree]);

  const renderNodes = (nodes: NavMenuNode[], depth: number) => {
    return nodes.map((n) => {
      if (n.menuType === 'DIRECTORY') {
        const open = openMap[n.id] ?? true;
        const active = subtreeHasActivePath(n, location.pathname);
        return (
          <div
            key={n.id}
            className={cn(
              'mx-3 mt-2 overflow-hidden rounded-xl border',
              isLight ? 'border-slate-300/80 bg-white/70' : 'border-white/[0.06] bg-white/[0.02]',
              depth === 0 &&
                active &&
                (isLight ? 'border-cyan-500/35' : 'border-cyan-500/20'),
            )}
          >
            <button
              type="button"
              onClick={() =>
                setOpenMap((m) => ({ ...m, [n.id]: !open }))
              }
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-t-xl px-3 py-2.5 text-left transition-all duration-200 motion-reduce:transition-none',
                'text-sm font-medium',
                isLight
                  ? 'border border-transparent text-slate-600 hover:border-cyan-500/20 hover:bg-white hover:text-slate-900'
                  : 'border border-transparent text-zinc-400 hover:border-white/5 hover:bg-white/[0.04] hover:text-zinc-100',
                active &&
                  (isLight
                    ? 'border-cyan-500/35 bg-cyan-100/75 text-slate-900'
                    : 'border-cyan-500/25 bg-cyan-500/[0.08] text-zinc-200'),
              )}
              aria-expanded={open}
            >
              <span className="flex min-w-0 items-center gap-3">
                <NavIcon name={n.iconKey} className={isLight ? 'text-slate-500' : 'text-zinc-400'} />
                {n.title}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200',
                  isLight && 'text-slate-500',
                  open ? 'rotate-0' : '-rotate-90',
                )}
                aria-hidden
              />
            </button>
            {open && (
              <div
                className={cn(
                  'space-y-0.5 border-t px-0 pb-1.5 pt-0.5',
                  isLight ? 'border-slate-300/80' : 'border-white/[0.06]',
                )}
              >
                {renderNodes(n.children, depth + 1)}
              </div>
            )}
          </div>
        );
      }

      if (n.menuType === 'MENU' && n.path) {
        return (
          <NavLink
            key={n.id}
            to={n.path}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                linkClass({ isActive }),
                depth > 0 && 'mx-2 border-l-2 border-transparent pl-3',
                depth > 0 && isActive && 'border-l-cyan-400/70',
              )
            }
          >
            <NavIcon name={n.iconKey} />
            {n.title}
          </NavLink>
        );
      }

      if (n.menuType === 'EXTERNAL' && n.externalUrl) {
        return (
          <a
            key={n.id}
            href={n.externalUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onNavigate}
            className={cn(
              linkClass({ isActive: false }),
              depth > 0 && 'mx-2 pl-3',
            )}
          >
            <NavIcon name={n.iconKey} />
            {n.title}
          </a>
        );
      }

      return null;
    });
  };

  return (
    <>
      <div className={cn('flex h-16 items-center border-b px-6', isLight ? 'border-slate-300/90' : 'border-white/10')}>
        <BrandLogo isLight={isLight} />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto py-4">
        {tree.length === 0 ? (
          <p className={cn('mx-4 px-3 text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
            当前账号暂无可见菜单权限，请联系管理员。
          </p>
        ) : (
          renderNodes(tree, 0)
        )}
      </nav>

      <div className={cn('relative z-10 border-t p-4', isLight ? 'border-slate-300/90' : 'border-white/10')}>
        <p className={cn('text-[11px] leading-relaxed', isLight ? 'text-slate-500' : 'text-zinc-500')}>
          SystemHub v2.0
          <span className={cn('mt-1 block', isLight ? 'text-slate-400' : 'text-zinc-600')}>© 2026</span>
        </p>
      </div>
    </>
  );
}
